import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { fetchEmailLog, fetchEmailPreview } from '../services/emailAlertsApi';

/**
 * EmailLogModal — INVEXIS staff only (the menu item exists only when the server says the
 * signed-in account is on INVEXIS_ADMIN_EMAILS, and the API checks again).
 *
 * Two jobs, from the Email & Reports design:
 *   - "I never got my report": a business's last 100 emails — who, what, when, and whether each
 *     was sent, skipped (and why) or failed.
 *   - preview any email in the browser, built from the business's real data, WITHOUT sending it.
 */
const KIND_LABELS = {
  daily_pulse: 'Daily Pulse',
  radar_alert: 'Radar alert',
  weekly_report: 'Weekly Report',
  monthly_report: 'Monthly Report (part 1)',
  monthly_final: 'Monthly Report (part 2, month closed)',
  month_close_reminder: 'Close-the-month reminder',
  subscription: 'Subscription reminder',
  missed_tsr: 'Missed TSR',
  verify_email: 'Verify email',
  staff_invite: 'Staff invite',
  password_reset_code: 'Password reset code',
  password_reset_link: 'Password reset link',
  invexis_notice: 'Invexis team notice',
  invexis_expiry_list: 'Invexis expiry list',
};
const PREVIEW_KINDS = ['daily_pulse', 'radar_alert', 'weekly_report', 'monthly_report', 'monthly_final', 'month_close_reminder', 'subscription', 'missed_tsr'];
const SKIP_REASONS = {
  alerts_off: 'Email alerts switched off',
  type_off: 'This email switched off',
  duplicate: 'Already sent',
  no_data: 'No activity',
  no_recipient: 'No recipient',
};
const STATUS_COLORS = { sent: '#15803d', skipped: '#6b7280', failed: '#b91c1c', retrying: '#b45309', sending: '#2563eb' };

const fmtTime = (v) => (v ? new Date(v).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const inputStyle = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' };
const buttonStyle = { padding: '8px 14px', border: 'none', borderRadius: '6px', backgroundColor: '#1f2937', color: '#fff', fontSize: '13px', cursor: 'pointer' };

const EmailLogModal = ({ onClose }) => {
  const [tab, setTab] = useState('log');
  const [businessId, setBusinessId] = useState(() => localStorage.getItem('businessId') || '');
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [kind, setKind] = useState('weekly_report');
  const [date, setDate] = useState('');
  const [month, setMonth] = useState('');
  const [locked, setLocked] = useState(false);

  const loadLog = async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchEmailLog(businessId);
      if (!res || res.success === false) throw new Error((res && res.error) || 'Could not load the log');
      setLog(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const res = await fetchEmailPreview({ businessId, kind, date, month, locked: locked ? '1' : '' });
      if (!res || res.success === false) throw new Error((res && res.error) || 'Could not build the preview');
      setPreview(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const body = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Email log"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: '#fff', borderRadius: '10px', width: '100%', maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', flex: 1 }}>📧 Email Log</h3>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '12px 18px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
          <label style={{ fontSize: '13px', color: '#374151' }}>
            Business ID{' '}
            <input value={businessId} onChange={(e) => setBusinessId(e.target.value.replace(/\D/g, ''))} style={{ ...inputStyle, width: '90px' }} inputMode="numeric" />
          </label>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            {['log', 'preview'].map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)}
                style={{ ...buttonStyle, backgroundColor: tab === t ? '#1f2937' : '#e5e7eb', color: tab === t ? '#fff' : '#111827' }}>
                {t === 'log' ? 'Last 100 emails' : 'Preview an email'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
          {error && <div role="alert" style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '8px' }}>{error}</div>}

          {tab === 'log' && (
            <>
              <button type="button" onClick={loadLog} disabled={!businessId || loading} style={buttonStyle}>
                {loading ? 'Loading…' : 'Load emails'}
              </button>
              {log && log.installed === false && (
                <p style={{ fontSize: '13px', color: '#92400e' }}>
                  The email log is not installed on this server yet (run scripts/run-email-log-migration.js --apply). Emails are still sent.
                </p>
              )}
              {log && log.installed !== false && (
                <div style={{ overflowX: 'auto', marginTop: '10px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                        {['When', 'To', 'Email', 'Result', 'Tries', 'Detail'].map((h) => (
                          <th key={h} style={{ padding: '6px', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(log.rows || []).length === 0 && (
                        <tr><td colSpan={6} style={{ padding: '10px', color: '#6b7280' }}>No emails recorded for this business yet.</td></tr>
                      )}
                      {(log.rows || []).map((r) => (
                        <tr key={r.log_id} style={{ borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>
                          <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{fmtTime(r.sent_at || r.created_at)}</td>
                          <td style={{ padding: '6px', wordBreak: 'break-all' }}>{r.recipient || '—'}</td>
                          <td style={{ padding: '6px' }}>
                            {KIND_LABELS[r.email_kind] || r.email_kind}
                            {r.subject && <div style={{ color: '#6b7280' }}>{r.subject}</div>}
                          </td>
                          <td style={{ padding: '6px', color: STATUS_COLORS[r.status] || '#111827', fontWeight: 600 }}>{r.status}</td>
                          <td style={{ padding: '6px' }}>{r.attempts}</td>
                          <td style={{ padding: '6px', color: '#6b7280' }}>
                            {r.skip_reason ? (SKIP_REASONS[r.skip_reason] || r.skip_reason) : ''}
                            {r.status === 'retrying' && r.next_attempt_at ? `Next try ${fmtTime(r.next_attempt_at)}. ` : ''}
                            {r.last_error || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {tab === 'preview' && (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={kind} onChange={(e) => setKind(e.target.value)} style={inputStyle}>
                  {PREVIEW_KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
                </select>
                {['daily_pulse', 'radar_alert', 'missed_tsr', 'weekly_report'].includes(kind) && (
                  <label style={{ fontSize: '12px' }}>Day <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} /></label>
                )}
                {['monthly_report', 'monthly_final', 'month_close_reminder'].includes(kind) && (
                  <label style={{ fontSize: '12px' }}>Month <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={inputStyle} /></label>
                )}
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} /> As a lapsed subscription sees it
                </label>
                <button type="button" onClick={loadPreview} disabled={!businessId || loading} style={buttonStyle}>
                  {loading ? 'Building…' : 'Build preview'}
                </button>
              </div>
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '6px 0 0 0' }}>Built from the business's real data. Nothing is sent.</p>
              {preview && !preview.html && (
                <p style={{ fontSize: '13px', color: '#374151' }}>{preview.note || 'Nothing to preview.'}</p>
              )}
              {preview && preview.html && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>Subject:</strong> {preview.subject}</div>
                  <iframe
                    title="Email preview"
                    sandbox=""
                    srcDoc={preview.html}
                    style={{ width: '100%', height: '60vh', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(body, document.body);
};

export default EmailLogModal;
